import { forwardRef, useEffect, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Loader2, Sparkles, X } from 'lucide-react';

export function cn(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

type Variant = 'primary' | 'ghost' | 'soft' | 'ai' | 'danger' | 'outline';

export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md' | 'lg'; loading?: boolean; icon?: ReactNode }>(
  function Button({ variant = 'soft', size = 'md', loading, icon, className, children, disabled, ...rest }, ref) {
    const base = 'relative inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-all duration-150 active:scale-[0.97] disabled:opacity-50 disabled:pointer-events-none select-none whitespace-nowrap';
    const sizes = { sm: 'h-7 px-2.5 text-[12.5px]', md: 'h-9 px-3.5 text-[13.5px]', lg: 'h-11 px-5 text-[14.5px]' };
    const variants: Record<Variant, string> = {
      primary: 'bg-accent text-accent-fg hover:brightness-110 shadow-sm glow',
      ghost: 'text-muted hover:text-fg hover:bg-line',
      soft: 'bg-line text-fg hover:bg-line-strong',
      outline: 'border border-line-strong text-fg hover:bg-line',
      ai: 'ai-gradient text-white shadow-lg glow hover:brightness-110',
      danger: 'bg-danger/90 text-white hover:bg-danger',
    };
    return (
      <button ref={ref} data-variant={variant} className={cn('dts-btn', base, sizes[size], variants[variant], className)} disabled={disabled || loading} {...rest}>
        {loading ? <Loader2 className="size-4 animate-spin" /> : icon}
        {children}
      </button>
    );
  },
);

export const IconButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { label: string; active?: boolean; size?: 'sm' | 'md' }>(
  function IconButton({ label, active, className, size = 'md', children, ...rest }, ref) {
    return (
      <button
        ref={ref}
        title={label}
        aria-label={label}
        className={cn(
          'inline-flex items-center justify-center rounded-lg transition-all duration-150 active:scale-90',
          size === 'sm' ? 'size-7 [&_svg]:size-[15px]' : 'size-8 [&_svg]:size-[17px]',
          active ? 'bg-accent/15 text-accent' : 'text-muted hover:text-fg hover:bg-line',
          className,
        )}
        {...rest}
      >
        {children}
      </button>
    );
  },
);

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return (
    <input
      ref={ref}
      className={cn(
        'h-9 w-full rounded-xl border border-line bg-raised/60 px-3 text-[13.5px] text-fg placeholder:text-faint outline-none transition focus:border-accent/60 focus:ring-4 focus:ring-accent/10',
        className,
      )}
      {...rest}
    />
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      className={cn(
        'w-full rounded-xl border border-line bg-raised/60 px-3 py-2 text-[13.5px] text-fg placeholder:text-faint outline-none transition focus:border-accent/60 focus:ring-4 focus:ring-accent/10 resize-y',
        className,
      )}
      {...rest}
    />
  );
});

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded-md border border-line-strong bg-raised px-1 font-sans text-[10.5px] font-medium text-muted">{children}</kbd>;
}

export function Chip({ children, color, className, onClick, active }: { children: ReactNode; color?: string; className?: string; onClick?: () => void; active?: boolean }) {
  const Tag = onClick ? 'button' : 'span';
  return (
    <Tag
      onClick={onClick}
      className={cn(
        'pill badge inline-flex h-6 items-center gap-1 rounded-full px-2.5 text-[11.5px] font-medium transition',
        onClick && 'hover:brightness-110 active:scale-95',
        active ? 'is-active bg-accent text-accent-fg' : !color && 'bg-line text-muted',
        className,
      )}
      style={color && !active ? { background: tint(color), color } : undefined}
    >
      {children}
    </Tag>
  );
}

export function Toggle({ checked, onChange, label, hint }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode; hint?: ReactNode }) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-4 py-2">
      {(label || hint) && (
        <span>
          <span className="block text-[13.5px] text-fg">{label}</span>
          {hint && <span className="mt-0.5 block text-[12px] text-muted">{hint}</span>}
        </span>
      )}
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cn('switch relative mt-0.5 h-[22px] w-[38px] shrink-0 rounded-full transition-colors', checked ? 'bg-accent' : 'bg-line-strong')}
      >
        <motion.span layout transition={{ type: 'spring', stiffness: 600, damping: 32 }} className={cn('switch-knob absolute top-[3px] size-4 rounded-full bg-white shadow', checked ? 'right-[3px]' : 'left-[3px]')} />
      </button>
    </label>
  );
}

/** A colour at a low strength, for badge backgrounds; works with hex values and var(--…) tokens alike. */
export function tint(color: string, percent = 12): string {
  return `color-mix(in srgb, ${color} ${percent}%, transparent)`;
}

/** A displayed value: its digit runs (and : . -) get .num, the Dossier's seven-segment face in light mode; words stay in the sans. */
export function Num({ children }: { children: string | number }) {
  const parts = String(children).split(/(\d[\d:.\-]*)/);
  return <>{parts.map((p, i) => (i % 2 ? <span key={i} className="num">{p}</span> : p))}</>;
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('size-4 animate-spin text-muted', className)} />;
}

export function AiBadge({ busy, children = 'AI' }: { busy?: boolean; children?: ReactNode }) {
  return (
    <span className="pill badge inline-flex items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-[11px] font-semibold text-accent">
      <Sparkles className={cn('size-3 text-accent', busy && 'animate-pulse')} />
      <span className="ai-text">{children}</span>
    </span>
  );
}

export function Modal({ open, onClose, children, width = 560, title }: { open: boolean; onClose: () => void; children: ReactNode; width?: number; title?: ReactNode }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[70] flex items-center justify-center p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            className="glass relative max-h-[86vh] w-full overflow-hidden rounded-2xl bg-raised/95 shadow-2xl"
            style={{ maxWidth: width }}
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 8 }}
            transition={{ type: 'spring', stiffness: 420, damping: 32 }}
          >
            {title && (
              <div className="flex items-center justify-between border-b border-line px-5 py-3.5">
                <div className="text-[15px] font-semibold">{title}</div>
                <IconButton label="Close" onClick={onClose} size="sm">
                  <X />
                </IconButton>
              </div>
            )}
            <div className="max-h-[calc(86vh-56px)] overflow-y-auto">{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

export function Empty({ icon, title, hint, children }: { icon: ReactNode; title: ReactNode; hint?: ReactNode; children?: ReactNode }) {
  return (
    <div className="dossier-grid flex h-full flex-col items-center justify-center gap-3 p-10 text-center">
      <div className="flex size-14 items-center justify-center rounded-2xl bg-line text-muted [&_svg]:size-6">{icon}</div>
      <div className="text-[15px] font-semibold text-fg">{title}</div>
      {hint && <div className="max-w-xs text-[13px] text-muted">{hint}</div>}
      {children}
    </div>
  );
}
